import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FolderCheck, Building2, Zap, Star, Target } from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { stats as defaultStats } from '../data/mockData';

const ICONS = { FolderCheck, Building2, Zap, Star, Target };

// Parse a value like "50+", "< 1.5s", "4.9 / 5" — animate leading number
const parseValue = (v) => {
  const s = String(v || '');
  const match = s.match(/([\d.]+)/);
  if (!match) return { num: null, prefix: s, suffix: '' };
  const num = parseFloat(match[1]);
  const idx = s.indexOf(match[1]);
  return {
    num,
    prefix: s.slice(0, idx),
    suffix: s.slice(idx + match[1].length),
    isFloat: match[1].includes('.'),
  };
};

const AnimatedNumber = ({ value }) => {
  const { num, prefix, suffix, isFloat } = parseValue(value);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [display, setDisplay] = useState(num !== null ? 0 : value);

  useEffect(() => {
    if (num === null || !inView) return;
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { setDisplay(num); return; }
    const start = performance.now();
    const duration = 1400;
    let raf;
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(isFloat ? +(num * eased).toFixed(1) : Math.round(num * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, num, isFloat]);

  return (
    <span
      ref={ref}
      className="block font-display font-medium tracking-tight text-white text-4xl md:text-5xl leading-[1.05] whitespace-nowrap"
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {prefix}{num === null ? '' : display}{suffix}
    </span>
  );
};

const AnimatedStats = () => {
  const { get } = useCMS();
  const stats = get('stats') || defaultStats;

  return (
    <section className="relative py-24 lg:py-28 bg-[#050914] border-t border-white/5" data-testid="animated-stats">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-4">
              <span className="w-8 h-px bg-white/30" />
              By The Numbers
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-white leading-[1.05] max-w-2xl">
              Focused work, measurable outcomes.
            </h2>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-white/35 font-bold">Placeholder values · editable in CMS</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden">
          {stats.map((s, i) => {
            const Icon = ICONS[s.icon] || Star;
            return (
              <motion.div
                key={s.id || i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="bg-[#050914] p-6 lg:p-8 flex flex-col justify-between min-h-[220px]"
                data-testid={`stat-${s.id || i}`}
              >
                <Icon className="w-5 h-5 text-white/40" />
                <div>
                  {/* Reserve vertical space so number expansion never nudges page scroll */}
                  <div className="min-h-[3.25rem] md:min-h-[3.75rem] flex items-end overflow-hidden">
                    <AnimatedNumber value={s.value ?? s.number} />
                  </div>
                  <p className="mt-3 text-sm text-white/50 font-light">{s.label}</p>
                  {s.placeholder && (
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/25 font-bold">Placeholder</p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AnimatedStats;
